import { createRouter, createWebHistory } from "vue-router";
import { navigationGuard } from "./navigationGuard.js";
import { useUserStore } from "../store/user.js";

// Resolve the Customer Portal landing tab to the first one the user can write to,
// so the hub card never lands on an empty page or a tab the user can't access.
function customerPortalLanding() {
  const perms = useUserStore().permissions;
  const canWrite = (c) => {
    const a = perms?.[c];
    return !!a && (a.create?.access !== 'none' || a.update?.access !== 'none');
  };
  if (canWrite('customers')) return { name: 'admin.customer-portal.customers' };
  if (canWrite('customer_files')) return { name: 'admin.customer-portal.files' };
  if (canWrite('customer_info')) return { name: 'admin.customer-portal.info' };
  return { name: 'admin.customer-portal.customers' };
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      show_in_menu: true,
      meta: {
        title: 'Home',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 22V12H15V22M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      component: () => import("../views/HomeView.vue"),
    },
    {
      path: "/login",
      name: "login",
      component: () => import("../views/LoginView.vue"),
    },
    {
      path: "/auth/callback",
      name: "auth-callback",
      component: () => import("../views/AuthCallbackView.vue"),
    },
    {
      path: "/settings",
      name: "settings",
      meta: {
        title: 'Settings',
        icon: '<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><g><path fill="none" d="M0 0h24v24H0z"></path><path d="M2 12c0-.865.11-1.703.316-2.504A3 3 0 0 0 4.99 4.867a9.99 9.99 0 0 1 4.335-2.505 3 3 0 0 0 5.348 0 9.99 9.99 0 0 1 4.335 2.505 3 3 0 0 0 2.675 4.63c.206.8.316 1.638.316 2.503 0 .865-.11 1.703-.316 2.504a3 3 0 0 0-2.675 4.629 9.99 9.99 0 0 1-4.335 2.505 3 3 0 0 0-5.348 0 9.99 9.99 0 0 1-4.335-2.505 3 3 0 0 0-2.675-4.63C2.11 13.704 2 12.866 2 12zm4.804 3c.63 1.091.81 2.346.564 3.524.408.29.842.541 1.297.75A4.993 4.993 0 0 1 12 18c1.26 0 2.438.471 3.335 1.274.455-.209.889-.46 1.297-.75A4.993 4.993 0 0 1 17.196 15a4.993 4.993 0 0 1 2.77-2.25 8.126 8.126 0 0 0 0-1.5A4.993 4.993 0 0 1 17.195 9a4.993 4.993 0 0 1-.564-3.524 7.989 7.989 0 0 0-1.297-.75A4.993 4.993 0 0 1 12 6a4.993 4.993 0 0 1-3.335-1.274 7.99 7.99 0 0 0-1.297.75A4.993 4.993 0 0 1 6.804 9a4.993 4.993 0 0 1-2.77 2.25 8.126 8.126 0 0 0 0 1.5A4.993 4.993 0 0 1 6.805 15zM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"></path></g></svg>',
      },
      component: () => import("../views/SettingsView.vue"),
    },
    {
      path: "/company",
      name: "company",
      redirect: '/',
      show_in_menu: true,
      meta: {
        title: 'Company',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 21V7L13 3V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M19 21V11L13 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 9V9.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M9 13V13.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M9 17V17.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
      },
      children: [
        {
          path: "employees",
          name: "company.employees",
          show_in_menu: true,
          meta: {
            title: 'Employees',
          },
          component: () => import("../views/company/Employees.vue"),
        },
        {
          path: "floorplan",
          name: "company.floorplan",
          show_in_menu: true,
          meta: {
            title: 'Floorplan',
          },
          component: () => import("../views/company/MeetingRooms.vue")
        },
        {
          path: "storage",
          name: "company.storage",
          show_in_menu: true,
          meta: {
            title: 'Storage Plan',
          },
          component: () => import("../views/company/Lagerplan.vue")
        },
        {
          path: "orgchart",
          name: "company.orgchart",
          show_in_menu: true,
          meta: {
            title: 'Org Chart',
          },
          component: () => import("../views/company/OrgChart.vue")
        },
        {
          path: "quickconnect",
          name: "company.quickConnect",
          show_in_menu: true,
          meta: {
            title: 'Quick Connect',
          },
          component: () => import("../views/company/QuickConnect.vue")
        },
        {
          path: "quicklinks",
          name: "company.quicklinks",
          show_in_menu: true,
          meta: {
            title: 'Quick Links',
          },
          component: () => import("../views/company/QuickLinks.vue")
        }
      ],
    },
    {
      path: "/productivity",
      name: "productivity",
      redirect: '/',
      show_in_menu: true,
      meta: {
        title: 'Productivity',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 11L12 14L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      children: [
        {
          path: "forms",
          name: "productivity.forms",
          show_in_menu: true,
          meta: {
            title: 'Digital Forms',
          },
          component: () => import("../views/digitalForms/Overview.vue"),
        },
        {
          path: "forms/new",
          name: "productivity.forms.new",
          component: () => import("../views/digitalForms/Forms.vue"),
        },
        {
          path: "forms/edit/:id",
          name: "productivity.forms.edit",
          component: () => import("../views/digitalForms/Edit.vue"),
        },
        {
          path: "forms/completed",
          name: "productivity.forms.completed",
          component: () => import("../views/digitalForms/Completed.vue"),
        },
        {
          path: "microsoft-forms",
          name: "productivity.microsoft-forms",
          show_in_menu: true,
          meta: {
            title: 'Microsoft Forms',
          },
          component: () => import("../views/company/TeamsForms.vue")
        },
        {
          path: "documents",
          name: "productivity.documents",
          show_in_menu: true,
          meta: {
            title: 'Documents',
          },
          component: () => import("../views/productivity/Documents.vue")
        }
      ]
    },
    {
      path: "/sales",
      name: "sales",
      redirect: '/',
      show_in_menu: false,
      meta: {
        title: 'Sales',
        collection: "events",
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      children: [
        {
          path: "events",
          name: "sales.events",
          show_in_menu: true,
          meta: {
            title: 'Events',
            collection: "events"
          },
          component: () => import("../views/sales/Overview.vue"),
        },
        {
          path: "events/:id",
          name: "sales.events.edit",
          component: () => import("../views/sales/Edit.vue"),
        },
        {
          path: "price-requests",
          name: "sales.price-requests",
          show_in_menu: true,
          meta: {
            title: 'Price Requests',
            collection: "price_request"
          },
          component: () => import("../views/sales/PriceRequests.vue"),
        },
        {
          path: "price-requests/:id",
          name: "sales.price-requests.edit",
          component: () => import("../views/sales/PriceRequestsEdit.vue"),
        },
      ],
    },
    {
      path: "/marketing",
      name: "marketing",
      redirect: '/',
      show_in_menu: false,
      meta: {
        title: 'Marketing',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 12C2 17.5228 6.47715 22 12 22C13.6569 22 15 20.6569 15 19V18.5C15 18.0356 15 17.8034 15.0257 17.6084C15.2029 16.2622 16.2622 15.2029 17.6084 15.0257C17.8034 15 18.0356 15 18.5 15H19C20.6569 15 22 13.6569 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12Z" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 13C7.55228 13 8 12.5523 8 12C8 11.4477 7.55228 11 7 11C6.44772 11 6 11.4477 6 12C6 12.5523 6.44772 13 7 13Z" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 9C16.5523 9 17 8.55228 17 8C17 7.44772 16.5523 7 16 7C15.4477 7 15 7.44772 15 8C15 8.55228 15.4477 9 16 9Z" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 8C10.5523 8 11 7.55228 11 7C11 6.44772 10.5523 6 10 6C9.44772 6 9 6.44772 9 7C9 7.55228 9.44772 8 10 8Z" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      children: [
        {
          path: "resources",
          name: "marketing.resources",
          show_in_menu: true,
          meta: {
            title: 'MarCom',
          },
          component: () => import("../views/marketing/resourceHub/ResourceHub.vue"),
        },
        {
          path: "identity",
          name: "marketing.identity",
          show_in_menu: true,
          meta: {
            title: 'Corporate Identity',
          },
          component: () => import("../views/marketing/Identity.vue"),
        },
        {
          path: "requests",
          name: "marketing.requests",
          show_in_menu: true,
          meta: {
            title: 'Marketing Request',
          },
          component: () => import("../views/marketing/marketingRequest/Overview.vue"),
        },
        {
          path: "requests/:id",
          name: "marketing.requests.edit",
          component: () => import("../views/marketing/marketingRequest/Edit.vue"),
        },
        {
          path: "photo-requests",
          name: "marketing.photo-requests",
          show_in_menu: false,
          meta: {
            title: 'Photo Requests',
            collection: "orbit_photo_requests"
          },
          component: () => import("../views/marketing/photoRequests/Overview.vue"),
        },
        {
          path: "scripts",
          name: "marketing.scripts",
          show_in_menu: true,
          meta: {
            title: 'Video Scripts',
            collection: "script"
          },
          component: () => import("../views/marketing/scripts/Overview.vue"),
        },
        {
          path: "scripts/edit/:id",
          name: "marketing.scripts.edit",
          component: () => import("../views/marketing/scripts/Edit.vue"),
        },
        {
          path: "scripts/edit/:id",
          name: "marketing.scripts.edit",
          component: () => import("../views/marketing/scripts/Edit.vue"),
        },
        {
          path: "signature",
          name: "marketing.signature",
          show_in_menu: true,
          meta: {
            title: 'Signature',
          },
          component: () => import("../views/marketing/Signature.vue"),
        },
        {
          path: "reviews/:id",
          name: "marketing.reviews.edit",
          meta: {
            collection: 'orbit_review'
          },
          component: () => import("../views/reviews/Edit.vue"),
        },
      ],
    },
    {
      path: "/software",
      name: "software",
      show_in_menu: false,
      meta: {
        title: 'Software',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 17L10 11L4 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 19H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      component: () => import("../views/software/Software.vue"),
    },
    {
      path: "/notifications",
      name: "inbox",
      show_in_menu: false,
      meta: {
        title: 'Inbox',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.35419 21C10.0593 21.6224 10.9856 22 12 22C13.0145 22 13.9407 21.6224 14.6458 21M18 8C18 6.4087 17.3679 4.88258 16.2427 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.8826 2.63214 7.75738 3.75736C6.63216 4.88258 6.00002 6.4087 6.00002 8C6.00002 11.0902 5.22049 13.206 4.34968 14.6054C3.61515 15.7859 3.24788 16.3761 3.26134 16.5408C3.27626 16.7231 3.31488 16.7926 3.46179 16.9016C3.59448 17 4.19261 17 5.38887 17H18.6112C19.8074 17 20.4056 17 20.5382 16.9016C20.6852 16.7926 20.7238 16.7231 20.7387 16.5408C20.7522 16.3761 20.3849 15.7859 19.6504 14.6054C18.7795 13.206 18 11.0902 18 8Z" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      component: () => import("../views/NotificationsView.vue"),

    },
    {
      path: "/admin",
      name: "admin",
      component: () => import("../views/admin/AdminWrapper.vue"),
      meta: {
        title: 'Admin',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 12L11 14L15 10" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      },
      show_in_menu: false,
      children: [
        {
          path: "",
          name: "admin.index",
          component: () => import("../views/admin/AdminIndex.vue"),
          meta: { title: 'Admin' }
        },
        {
          path: "idcard",
          name: "admin.idcard",
          show_in_menu: true,
          meta: {
            title: 'ID Card',
            collection: "orbit_news",
            requiresWrite: true
          },
          component: () => import("../views/company/IdCard.vue"),
        },
          {
          path: "news",
          name: "admin.news",
          show_in_menu: true,
          meta: {
            title: 'News',
            collection: "orbit_news",
            requiresWrite: true
          },
          component: () => import("../views/company/news/Overview.vue"),
        },
        {
          path: "news/new",
          name: "admin.news.new",
          meta: {
            title: 'Create News',
            collection: "orbit_news",
            requiresWrite: true
          },
          component: () => import("../views/company/news/Edit.vue"),
        },
        {
          path: "news/:id",
          name: "admin.news.edit",
          meta: {
            title: 'Edit News',
            collection: "orbit_news",
            requiresWrite: true
          },
          component: () => import("../views/company/news/Edit.vue"),
        },
        {
          path: "documents",
          name: "admin.documents",
          show_in_menu: true,
          meta: {
            title: 'Documents',
            collection: "orbit_documents",
            requiresWrite: true
          },
          component: () => import("../views/company/documents/Overview.vue"),
        },
        {
          path: "documents/new",
          name: "admin.documents.new",
          meta: {
            title: 'Create Document',
            collection: "orbit_documents",
            requiresWrite: true
          },
          component: () => import("../views/company/documents/Edit.vue"),
        },
        {
          path: "documents/:id",
          name: "admin.documents.edit",
          meta: {
            title: 'Edit Document',
            collection: "orbit_documents",
            requiresWrite: true
          },
          component: () => import("../views/company/documents/Edit.vue"),
        },
        {
          path: "users",
          name: "admin.users",
          show_in_menu: true,
          meta: {
            title: 'Users',
            collection: "directus_users",
            requiresWrite: true,
            requiresFullAccess: true
          },
          component: () => import("../views/admin/users/Overview.vue"),
        },
        {
          path: "users/:id",
          name: "admin.users.edit",
          meta: {
            title: 'Edit User',
            collection: "directus_users",
            requiresWrite: true,
            requiresFullAccess: true
          },
          component: () => import("../views/admin/users/Edit.vue"),
        },
        {
          path: "customer-portal",
          name: "admin.customer-portal",
          show_in_menu: true,
          meta: {
            title: 'Customer Portal'
          },
          component: () => import("../views/admin/customerPortal/CustomerPortal.vue"),
          children: [
            {
              path: "",
              name: "admin.customer-portal.index",
              redirect: customerPortalLanding
            },
            {
              path: "customers",
              name: "admin.customer-portal.customers",
              meta: { title: 'Customers', collection: "customers", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/CustomersOverview.vue"),
            },
            {
              path: "customers/new",
              name: "admin.customer-portal.customers.new",
              meta: { title: 'New Customer', collection: "customers", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/CustomersEdit.vue"),
            },
            {
              path: "customers/:id",
              name: "admin.customer-portal.customers.edit",
              meta: { title: 'Edit Customer', collection: "customers", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/CustomersEdit.vue"),
            },
            {
              path: "software-files",
              name: "admin.customer-portal.files",
              meta: { title: 'Software Files', collection: "customer_files", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/SoftwareFilesOverview.vue"),
            },
            {
              path: "software-files/new",
              name: "admin.customer-portal.files.new",
              meta: { title: 'New File', collection: "customer_files", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/SoftwareFilesEdit.vue"),
            },
            {
              path: "software-files/:id",
              name: "admin.customer-portal.files.edit",
              meta: { title: 'Edit File', collection: "customer_files", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/SoftwareFilesEdit.vue"),
            },
            {
              path: "software-info",
              name: "admin.customer-portal.info",
              meta: { title: 'Software Info', collection: "customer_info", requiresWrite: true },
              component: () => import("../views/admin/customerPortal/SoftwareInfo.vue"),
            },
          ],
        },
        {
          path: "polls",
          name: "admin.polls",
          show_in_menu: true,
          meta: {
            title: 'Polls',
            collection: "orbit_polls",
            requiresWrite: true
          },
          component: () => import("../views/admin/polls/Overview.vue"),
        },
        {
          path: "polls/new",
          name: "admin.polls.new",
          meta: {
            title: 'Create Poll',
            collection: "orbit_polls",
            requiresWrite: true
          },
          component: () => import("../views/company/polls/Edit.vue"),
        },
        {
          path: "polls/:id",
          name: "admin.polls.edit",
          meta: {
            title: 'Edit Poll',
            collection: "orbit_polls",
            requiresWrite: true
          },
          component: () => import("../views/company/polls/Edit.vue"),
        },
      ],
    },
    {
      path: "/spaces/:spaceSlug",
      name: "space.landing",
      component: () => import("../views/spaces/SpaceLanding.vue"),
    },
    {
      path: "/spaces/:spaceSlug/:pageSlug",
      name: "space.page",
      component: () => import("../views/spaces/SpacePage.vue"),
    },
    {
      path: "/spaces/:spaceSlug/:pageSlug/:childSlug",
      name: "space.subpage",
      component: () => import("../views/spaces/SpacePage.vue"),
    },
    {
      path: "/brochure/:id",
      name: "brochure",
      show_in_menu: true,
      component: () => import("../views/brochure/GenerateView.vue"),
    },
    {
      path: '/:catchAll(.*)',
      redirect: '/'
    }
  ],
});

router.beforeEach(navigationGuard);

// L: Prefetch the resource-hub search index when the user enters any
// marketing-related route. Fire-and-forget — by the time they click into the
// resource hub the JSON is already warm in the HTTP / SW cache. Only kicks
// off once per session (the SW + Pinia cache handle later visits).
let _searchIndexPrefetched = false;
router.beforeEach((to) => {
  if (_searchIndexPrefetched) return;
  const looksLikeMarketing =
    to.path.startsWith('/marketing') ||
    /^\/spaces\/marketing(\/|$)/.test(to.path);
  if (looksLikeMarketing) {
    _searchIndexPrefetched = true;
    const apiUrl = import.meta.env.VITE_apiUrl;
    if (apiUrl) {
      fetch(`${apiUrl}/search-index/`, { credentials: 'include' }).catch(() => {});
    }
  }
});

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('error loading dynamically imported module') ||
      error.message.includes('Unable to preload CSS') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk')) {
    // Chunk loading failed (likely due to a new deployment), reload the page
    window.location.href = to.fullPath;
  }
});

export default router;
